import React, { useState, useCallback, useEffect, useRef } from "react"
import shortid from "shortid"
import PropTypes from "prop-types"
import { Link, navigate } from "gatsby"
import { Transition, SwitchTransition } from "react-transition-group"
import gsap from "gsap"

import colours from "../../styles/colours"
import TextField from "../ui/textField/TextField"
import Checkbox from "../ui/checkbox/Checkbox"
import Button from "../ui/button/Button"

import formFields from "./formFields"
import { copyStringToClipboard } from "../../helpers/helpers"

import style from "./SubscribeForm.module.css"

const SubscribeForm = ({ referrerId }) => {
  const form = useRef()
  const [inputData, setInputData] = useState({})
  // Invalid fields show warning
  const [invalidFields, setInvalidFields] = useState({})
  // Valid fields show emoji
  const [validFields, setValidFields] = useState({})
  // This ID is appended to the user's personal share link and saved in their contact details
  const userReferralId = useRef(shortid())

  // When the form is submitted the Form component is switched for a Success Message containing User's share link
  const [formSubmitted, setFormSubmitted] = useState(false)

  const submitForm = async e => {
    e.preventDefault()
    // return setFormSubmitted(true)
    // Custom field validation
    let valid = true
    let invalidFields = {}

    formFields.forEach(field => {
      if (field.required) {
        // console.log('field required');
        if (
          inputData[field.name] === undefined ||
          inputData[field.name] === "" ||
          inputData[field.name] === false
        ) {
          console.log("field not entered", field.name)
          valid = false
          invalidFields[field.name] = true
        }
      }
    })

    console.log("submitForm() invalid fields:", invalidFields)
    if (!valid) return setInvalidFields(invalidFields)

    // Created form data in x-www-form-urlencoded format.
    const formData = new URLSearchParams([...new FormData(form.current)])

    for (var value of formData.values()) {
      console.log(value)
    }

    console.log("formData", formData)
    // Submit Form Data manually (so that Mailchimp doesn't redirect us)
    const response = await fetch(
      "https://app.us8.list-manage.com/subscribe/post?u=d341ba14768feb20f12908a47&amp;id=b0d3956481",
      {
        method: "POST", // *GET, POST, PUT, DELETE, etc.
        mode: "no-cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        credentials: "omit", // include, *same-origin, omit
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        redirect: "follow", // manual, *follow, error
        referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
        body: formData, // body data type must match "Content-Type" header
      }
    )
      .then(response => {
        setInputData({})
        setFormSubmitted(true)
      })
      .catch(error => {
        console.log(error)
        alert(error)
      })
  }

  const updateField = useCallback(e => {
    e.persist()
    console.log("Input changed", e)
    setInputData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }, [])

  const validateInput = useCallback(
    e => {
      e.persist()
      // TODO: smarter validation
      let valid = true
      if (!e.target.value.length) valid = false
      setValidFields({ ...validFields, [e.target.name]: valid })
    },
    [inputData, validFields]
  )

  const updateCheckbox = useCallback((e, key) => {
    e.preventDefault()
    setInputData(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const inputElements = formFields.map(field => {
    switch (field.type) {
      default:
        return (
          <div key={field.name} className="animatingField">
            <TextField
              name={field.name}
              type={field.type}
              emoji={field.emoji}
              // TODO: Check these
              value={inputData[field.name] || ""}
              placeholder={field.placeholder}
              onChange={updateField}
              onBlur={validateInput}
              valid={validFields[field.name]}
              invalid={invalidFields[field.name]}
              customClass={style.field}
            />
          </div>
        )

      // Handled manually for now
      case "checkbox":
        return null

      //   return (
      //     <div
      //       className={[style.formRow, "animatingField"].join(" ")}
      //       key={field.name}
      //       style={rowStyle}
      //     >
      //       {/* <Link href={field.href}> */}
      //       <label>{field.label}</label>
      //       {/* </Link> */}
      //       <Checkbox
      //         checked={inputData[field.name]}
      //         pressed={e => updateCheckbox(e, field.name)}
      //         invalid={invalidFields[field.name]}
      //         disabled={false}
      //       />
      //     </div>
      //   )

      case "bot":
        return (
          <input
            key="bot"
            id="bot"
            type="text"
            name="b07"
            tabIndex="-1"
            value=""
            hidden={true}
          />
        )
    }
  })

  return (
    <section className={style.container}>
      <SwitchTransition mode="out-in">
        <Transition
          key={formSubmitted}
          timeout={400}
          onEnter={node => gsap.set(node, { xPercent: 100, opacity: 0 })}
          onEntering={node =>
            gsap.to(node, { duration: 0.4, xPercent: 0, opacity: 1 })
          }
          onExiting={node =>
            gsap.to(node, { duration: 0.4, xPercent: -100, opacity: 0.5 })
          }
        >
          {!formSubmitted ? (
            <div>
              <div className={style.offer}>
                <h3>Get your free £10 discount code</h3>
                <p>
                  You'll also be entered into a raffle to win one of 10 free
                  tops!
                </p>
              </div>
              <form
                ref={form}
                name="subscribe-form"
                className={style.form}
                action="post"
              >
                {/* Hidden form elements for referrals data */}
                <input
                  type="text"
                  name="REFERRAL"
                  hidden={true}
                  readOnly={true}
                  tabIndex="-2"
                  value={userReferralId.current}
                />
                <input
                  type="text"
                  name="REFERREDBY"
                  hidden={true}
                  tabIndex="-1"
                  readOnly={true}
                  value={referrerId}
                />
                {inputElements}

                <div>
                  <p className="offWhite" style={{ fontSize: "small" }}>
                    Please select the ways you would like to hear from Agaia:
                  </p>

                  <div style={{ display: "flex" }}>
                    <Checkbox
                      label="Email*"
                      name="gdpr[29433]"
                      checked={inputData["gdpr[29433]"]}
                      pressed={e => updateCheckbox(e, "gdpr[29433]")}
                      invalid={invalidFields["gdpr[29433]"]}
                    />
                    <Checkbox
                      label="Text Message"
                      name="gdpr[29441]"
                      checked={inputData["gdpr[29441]"]}
                      pressed={e => updateCheckbox(e, "gdpr[29441]")}
                      invalid={invalidFields["gdpr[29441]"]}
                    />
                  </div>
                </div>
                <Button
                  contained={true}
                  label="GET MY DISCOUNT"
                  onPress={submitForm}
                  contained={true}
                  backgroundColour={colours.green[40]}
                  colour="#FFF"
                  buttonStyle={{ width: "100%", marginTop: 16 }}
                />
                <p
                  className="offWhite"
                  style={{ fontSize: "small", opacity: 0.8 }}
                >
                  You can unsubscribe at any time. For information see our{" "}
                  <Link to="/legal" target="_blank" className="offWhite">
                    privacy policy.
                  </Link>
                </p>
              </form>
            </div>
          ) : (
            // Confirmation Message and unique share link
            <div className={style.submittedContainer}>
              <h2>Thanks for subscribing!</h2>
              <p>
                For each friend you introduce we'll give you an extra raffle
                ticket for a free top! With 10 Guaranteed Winners!
              </p>
              <Button
                label="Copy Personal Share Link"
                contained={true}
                contained={true}
                backgroundColour={colours.green[40]}
                colour="#FFF"
                onPress={() =>
                  copyStringToClipboard(
                    `https://agaia.netlify.app?ref=${userReferralId.current}`
                  )
                }
              />
            </div>
          )}
        </Transition>
      </SwitchTransition>
    </section>
  )
}

SubscribeForm.propTypes = {
  referrerId: PropTypes.string,
}
SubscribeForm.defaultProps = {}

export default SubscribeForm

// <!-- Begin Mailchimp Signup Form -->
// <div id="mc_embed_signup">
// <form action="https://app.us8.list-manage.com/subscribe/post?u=d341ba14768feb20f12908a47&amp;id=b0d3956481" method="post" id="mc-embedded-subscribe-form" name="mc-embedded-subscribe-form" class="validate" target="_blank" novalidate>
//     <div id="mc_embed_signup_scroll">

// <div class="mc-field-group">
// 	<label for="mce-EMAIL">Email Address </label>
// 	<input type="email" value="" name="EMAIL" class="required email" id="mce-EMAIL">
// </div>
// <div class="mc-field-group">
// 	<label for="mce-FNAME">First Name </label>
// 	<input type="text" value="" name="FNAME" class="required" id="mce-FNAME">
// </div>
// <div class="mc-field-group size1of2">
// 	<label for="mce-PHONE">Phone Number </label>
// 	<input type="text" name="PHONE" class="" value="" id="mce-PHONE">
// </div>
// <div class="mc-field-group">
// 	<label for="mce-REFERRAL">Referral Id </label>
// 	<input type="text" value="" name="REFERRAL" class="" id="mce-REFERRAL">
// </div>
// <div class="mc-field-group">
// 	<label for="mce-REFERREDBY">Referred By </label>
// 	<input type="text" value="" name="REFERREDBY" class="" id="mce-REFERREDBY">
// </div>
// <div id="mergeRow-gdpr" class="mergeRow gdpr-mergeRow content__gdprBlock mc-field-group">
//     <div class="content__gdpr">
//         <label>Marketing Permissions</label>
//         <p>Please select all the ways you would like to hear from Agaia:</p>
//         <fieldset class="mc_fieldset gdprRequired mc-field-group" name="interestgroup_field">
// 		<label class="checkbox subfield" for="gdpr_29433"><input type="checkbox" id="gdpr_29433" name="gdpr[29433]" value="Y" class="av-checkbox gdpr"><span>Email</span> </label><label class="checkbox subfield" for="gdpr_29441"><input type="checkbox" id="gdpr_29441" name="gdpr[29441]" value="Y" class="av-checkbox gdpr"><span>Telephone</span> </label>
//         </fieldset>
//         <p>You can unsubscribe at any time by clicking the link in the footer of our emails. For information about our privacy practices, please visit our website.</p>
//     </div>
//     <div class="content__gdprLegal">
//         <p>We use Mailchimp as our marketing platform. By clicking below to subscribe, you acknowledge that your information will be transferred to Mailchimp for processing. <a href="https://mailchimp.com/legal/" target="_blank">Learn more about Mailchimp's privacy practices here.</a></p>
//     </div>
// </div>
// 	<div id="mce-responses" class="clear">
// 		<div class="response" id="mce-error-response" style="display:none"></div>
// 		<div class="response" id="mce-success-response" style="display:none"></div>
// 	</div>    <!-- real people should not fill this in and expect good things - do not remove this or risk form bot signups-->
//     <div style="position: absolute; left: -5000px;" aria-hidden="true"><input type="text" name="b_d341ba14768feb20f12908a47_b0d3956481" tabindex="-1" value=""></div>
//     <div class="clear"><input type="submit" value="Subscribe" name="subscribe" id="mc-embedded-subscribe" class="button"></div>
//     </div>
// </form>
// </div>

// <!--End mc_embed_signup--></div>
